import { Pin, PinOff } from 'lucide-react';
import type { CSSProperties } from 'react';
import { ViewModeToggle } from '../../components/cards/cardKit';
import {
  PINS_VIEW_MODES,
  type PinsViewMode,
} from './pinsViewMode';

/** The pinned watch strip stays in normal document flow unless the trader
 * explicitly asks it to follow the viewport. Persist the opt-in so a trader
 * who prefers the original behavior gets it back after refresh. */
export const PINS_STICKY_KEY = 'g1mt_tracker_pins_sticky';

export function readPinsSticky(): boolean {
  try {
    return localStorage.getItem(PINS_STICKY_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writePinsSticky(enabled: boolean): void {
  try {
    localStorage.setItem(PINS_STICKY_KEY, String(enabled));
  } catch {
    // Private mode — the toggle still works for this session.
  }
}

export function pinnedPositionClass(enabled: boolean): string {
  return enabled ? 'sticky -bottom-6 -top-6 z-40' : '';
}

export function pinnedOrdersToolbarStyle(
  enabled: boolean,
  pinnedBlockHeight: number,
): CSSProperties | undefined {
  return enabled && pinnedBlockHeight > 0
    ? { top: `calc(${pinnedBlockHeight}px - 1.5rem)` }
    : undefined;
}

export function PinnedStickyToggle({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}) {
  const Icon = enabled ? Pin : PinOff;
  const label = enabled
    ? 'Stop keeping the Pinned section visible while scrolling'
    : 'Keep the Pinned section visible while scrolling';

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={enabled}
      title={label}
      onClick={() => onChange(!enabled)}
      className={`border-border flex items-center justify-center rounded-md border px-2 py-1 transition-colors ${
        enabled
          ? 'bg-blue-500/20 text-blue-400'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon size={14} />
    </button>
  );
}

/** Keep the scroll behavior control physically and semantically beside the
 * strip's view selector. The wrapper also prevents either control from
 * triggering the header's expand/collapse action. */
export function PinnedHeaderControls({
  viewMode,
  onViewModeChange,
  sticky,
  onStickyChange,
}: {
  viewMode: PinsViewMode;
  onViewModeChange: (mode: PinsViewMode) => void;
  sticky: boolean;
  onStickyChange: (enabled: boolean) => void;
}) {
  return (
    <div
      className="flex items-center gap-2"
      onClick={(event) => event.stopPropagation()}
    >
      <ViewModeToggle
        value={viewMode}
        onChange={onViewModeChange}
        modes={PINS_VIEW_MODES}
      />
      <PinnedStickyToggle enabled={sticky} onChange={onStickyChange} />
    </div>
  );
}
